/**
 * useTaskTimer — live task timer anchored to a server-persisted timestamp.
 *
 * Model:
 *   The DB row is the single source of truth:
 *     tasks.total_seconds     — accumulated seconds from finished sessions
 *     tasks.timer_started_at  — UTC instant the current session started
 *                               (NULL when the timer is not running)
 *
 *   Displayed total = total_seconds + (now - timer_started_at).
 *
 *   Because elapsed time is *derived* from a persisted timestamp instead of
 *   counted in component state, closing and reopening the task page can
 *   never roll the timer back — remounting simply recomputes the same
 *   formula. No unload/keepalive persistence hacks are required: the
 *   anchor is written once, when the timer starts.
 *
 * Behaviour:
 *   - Owner opening a non-done task auto-starts the timer (writes
 *     timer_started_at if not already running) and flips todo → in_progress.
 *   - Every viewer sees the timer tick live (it is derived state).
 *   - A 60s checkpoint folds elapsed time into total_seconds and re-anchors
 *     timer_started_at, so list views show fresh totals while running.
 *   - The timer PAUSES automatically when the tab is hidden or the component
 *     unmounts (navigation away) — elapsed is folded into total_seconds and
 *     the anchor is cleared. The auto-start effect re-fires when the tab
 *     becomes visible again, writing a fresh anchor so only active viewport
 *     time is counted.
 *   - On mount, if timer_started_at is older than 3× the checkpoint interval
 *     it is treated as a stale anchor from an unclean session end (e.g.
 *     browser force-closed). The anchor is reset to "now" WITHOUT folding
 *     the stale elapsed, so no background calendar time appears in the log.
 *   - stop() / markDone() fold elapsed into total_seconds and clear the
 *     anchor atomically.
 *
 * The optional onTaskPatch callback receives every optimistic field change
 * so the page can update its local task state instantly — realtime then
 * confirms with the authoritative row.
 */

import { useCallback, useEffect, useRef, useState } from 'react';
import { supabase } from '../api/supabase.js';
import { TASK_STATUS } from '../data/constants.js';

const CHECKPOINT_INTERVAL_MS = 60 * 1000;
// Anchors older than this on mount are treated as stale (unclean session end).
// The checkpoint keeps timer_started_at ≤60s old in a healthy session, so
// 3× gives a comfortable margin without mis-classifying legitimate anchors.
const STALE_ANCHOR_MS = CHECKPOINT_INTERVAL_MS * 3;

/** Seconds elapsed since an ISO timestamp; clock-skew safe (never negative). */
export function elapsedSince(isoTimestamp, nowMs = Date.now()) {
  if (!isoTimestamp) return 0;
  const startedMs = Date.parse(isoTimestamp);
  if (Number.isNaN(startedMs)) return 0;
  return Math.max(0, Math.floor((nowMs - startedMs) / 1000));
}

/** Display total for a task row under the timestamp-anchored model. */
export function computeLiveSeconds(task, nowMs = Date.now()) {
  if (!task) return 0;
  const base = Math.max(0, Number(task.total_seconds) || 0);
  return base + elapsedSince(task.timer_started_at, nowMs);
}

export function useTaskTimer({ task, isOwner, enabled = true, onTaskPatch }) {
  const [error, setError] = useState(null);
  // Tick counter — forces a re-render each second while running so the
  // derived liveSeconds recomputes. The value itself is irrelevant.
  const [, setTick] = useState(0);

  const isRunning = !!task?.timer_started_at && task?.status !== TASK_STATUS.DONE;
  const liveSeconds = computeLiveSeconds(task);

  const taskRef = useRef(task);
  taskRef.current = task;
  const patchRef = useRef(onTaskPatch);
  patchRef.current = onTaskPatch;
  const isOwnerRef = useRef(isOwner);
  isOwnerRef.current = isOwner;

  const applyPatch = useCallback((patch) => {
    patchRef.current?.(patch);
  }, []);

  /** Start (or resume) the timer — writes the anchor only if not running. */
  const start = useCallback(async () => {
    const t = taskRef.current;
    if (!t?.id || !isOwner || !enabled) return;
    if (t.timer_started_at) return; // already running — derived display is correct

    const nowIso = new Date().toISOString();
    const payload = { timer_started_at: nowIso };
    if (t.status === TASK_STATUS.TODO) payload.status = TASK_STATUS.IN_PROGRESS;

    // Optimistic: badge + timer respond instantly.
    applyPatch(payload);

    // `.is('timer_started_at', null)` makes the start atomic — if another
    // tab won the race, this write is a no-op and realtime reconciles us.
    const { data, error: updateError } = await supabase
      .from('tasks')
      .update(payload)
      .eq('id', t.id)
      .is('timer_started_at', null)
      .select('id, status, total_seconds, timer_started_at')
      .maybeSingle();

    if (updateError) {
      console.error('[ORIN] Failed to start timer:', updateError.message);
      setError(updateError.message);
      applyPatch({ timer_started_at: t.timer_started_at ?? null, status: t.status });
      return;
    }
    if (data) applyPatch(data);
  }, [isOwner, enabled, applyPatch]);

  // Keep a stable ref so the visibility handler can call start() without
  // being captured in the effect closure.
  const startRef = useRef(null);
  startRef.current = start;

  /** Fold elapsed seconds into total_seconds; optionally finalise fields. */
  const settle = useCallback(
    async (extra = {}) => {
      const t = taskRef.current;
      if (!t?.id) return;

      const newTotal = computeLiveSeconds(t);
      const payload = { total_seconds: newTotal, timer_started_at: null, ...extra };

      applyPatch(payload);

      const { error: updateError } = await supabase
        .from('tasks')
        .update(payload)
        .eq('id', t.id);

      if (updateError) {
        console.error('[ORIN] Failed to persist timer:', updateError.message);
        setError(updateError.message);
      }
    },
    [applyPatch],
  );

  /** Pause without finishing the task. */
  const stop = useCallback(async () => {
    if (!taskRef.current?.timer_started_at) return;
    await settle();
  }, [settle]);

  /** Finish: fold time, set done status + done_at, clear the anchor. */
  const markDone = useCallback(async () => {
    await settle({
      status: TASK_STATUS.DONE,
      done_at: new Date().toISOString(),
    });
  }, [settle]);

  // Auto-start when the owner opens a non-done task.
  // Guards:
  //   - Skip while the tab is hidden — the visibility handler triggers
  //     start() when the user returns, preventing background time accrual.
  //   - If timer_started_at is already set, check for a stale anchor (session
  //     that ended without a clean stop, e.g. browser force-closed). The
  //     checkpoint keeps the anchor ≤60s old in a live session, so anything
  //     older than STALE_ANCHOR_MS is definitely dead. Reset the anchor to
  //     "now" WITHOUT folding in the stale elapsed time.
  useEffect(() => {
    if (!isOwner || !enabled || !task?.id) return;
    if (task.status === TASK_STATUS.DONE) return;
    if (document.visibilityState !== 'visible') return;

    if (task.timer_started_at) {
      const staleMs = Date.now() - Date.parse(task.timer_started_at);
      if (staleMs > STALE_ANCHOR_MS) {
        // Discard the stale elapsed — only reset the anchor clock to now.
        const nowIso = new Date().toISOString();
        applyPatch({ timer_started_at: nowIso });
        supabase.from('tasks').update({ timer_started_at: nowIso }).eq('id', task.id);
      }
      // Anchor is fresh (or just repaired) — derived display is correct.
      return;
    }

    start();
    // Intentionally keyed on task id/anchor only — start() is stable.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [task?.id, task?.timer_started_at, isOwner, enabled]);

  // 1 Hz tick while running (any viewer — display is derived for everyone).
  useEffect(() => {
    if (!isRunning) return undefined;
    const id = setInterval(() => setTick((n) => n + 1), 1000);
    return () => clearInterval(id);
  }, [isRunning]);

  // Pause the timer when the tab is hidden so idle time is never counted as
  // active work. On return the anchor is re-written by start() via startRef.
  useEffect(() => {
    const onVisibilityChange = () => {
      if (document.visibilityState === 'hidden') {
        // Fold elapsed + clear anchor. The auto-start effect is guarded by
        // visibilityState so it won't restart while the tab stays hidden.
        const t = taskRef.current;
        if (!isOwnerRef.current || !t?.timer_started_at || !t?.id) return;
        const payload = { total_seconds: computeLiveSeconds(t), timer_started_at: null };
        patchRef.current?.(payload);
        supabase.from('tasks').update(payload).eq('id', t.id);
      } else {
        // Re-sync display tick (browsers throttle intervals in background tabs)
        // then restart the timer — start() is a no-op if already running or
        // if the task is done/disabled.
        setTick((n) => n + 1);
        startRef.current?.();
      }
    };
    document.addEventListener('visibilitychange', onVisibilityChange);
    return () => document.removeEventListener('visibilitychange', onVisibilityChange);
  }, []);

  // Owner-side checkpoint: fold progress into total_seconds every minute so
  // dashboards/lists stay fresh. The anchor is re-set in the same write, so
  // the display invariant (total + now - anchor) is preserved exactly.
  useEffect(() => {
    if (!isOwner || !isRunning || !task?.id) return undefined;

    const id = setInterval(async () => {
      const t = taskRef.current;
      if (!t?.timer_started_at) return;
      const nowIso = new Date().toISOString();
      const payload = {
        total_seconds: computeLiveSeconds(t),
        timer_started_at: nowIso,
      };
      applyPatch(payload);
      const { error: cpError } = await supabase
        .from('tasks')
        .update(payload)
        .eq('id', t.id);
      if (cpError) {
        console.error('[ORIN] Timer checkpoint failed:', cpError.message);
      }
    }, CHECKPOINT_INTERVAL_MS);

    return () => clearInterval(id);
  }, [isOwner, isRunning, task?.id, applyPatch]);

  // Fold timer on unmount (navigation away) so the next visit starts from
  // the correct accumulated total and no background time is counted.
  useEffect(() => {
    return () => {
      const t = taskRef.current;
      if (!isOwnerRef.current || !t?.timer_started_at || !t?.id) return;
      // Fire-and-forget — we cannot await in a cleanup function.
      supabase
        .from('tasks')
        .update({ total_seconds: computeLiveSeconds(t), timer_started_at: null })
        .eq('id', t.id);
    };
  }, []);

  return {
    liveSeconds,
    isRunning,
    start,
    stop,
    markDone,
    error,
  };
}
